import { EmpresaDTOModel } from "@/models/empresa/chamadas/empresaDTOModel";
import { atualizarEmpresa, cadastrarEmpresa } from "@/services/empresaService";
import * as Icon from "@ant-design/icons";
import { Button, Col, Divider, Flex, Form, Image, Input, Modal, Row, Select, Space, Tooltip, Upload, UploadFile, UploadProps } from "antd";
import { MaskedInput } from "antd-mask-input";
import TextArea from "antd/lib/input/TextArea";
import { useEffect, useMemo, useRef, useState } from "react";
import { EmpresaModel } from "../../../../models/empresa/empresaModel";
import { EstadoModel } from "../../../../models/localizacao/estadoModel";
import { PessoaModel } from "../../../../models/pessoa/pessoaModel";
import { setTitle } from "../../../../redux/slices/configuracoes/configuracoesSlice";
import { setPageEmpresa } from "../../../../redux/slices/empresa/empresaSlice";
import { buscarCep } from "../../../../services/localizacaoService";
import { buscarPessoa } from "../../../../services/pessoaService";
import { sendImageToAmazonS3 } from "../../../../util/functions/sendImageToAmazonS3";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { mascaraCelular, mascaraCep } from "../../../../util/mascaras";
import { toastError } from "../../../../util/toast";
import "./style/index.scss";

const CadastrarPage = () => {
  //estados globais
  const localizacaoState = useAppSelector((state) => state.localizacao);
  const empresaState = useAppSelector((state) => state.empresa);
  const grupoPlanoContaFinanceiroState = useAppSelector((state) => state.grupoPlanoContaFinanceiro);
  const categoriaEmpresaState = useAppSelector((state) => state.categoriaEmpresa);
  const pessoaState = useAppSelector((state) => state.pessoa);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();

  const [inputCep, setInputCep] = useState<string>("");
  const [tipo, setTipo] = useState<boolean>(false);
  const [idCategoriaEmpresaSelecionado, setIdCategoriaEmpresaSelecionado] = useState<number>(0);

  const [previewOpenMaior, setPreviewOpenMaior] = useState(false);
  const [previewImgMaior, setPreviewImgMaior] = useState("");
  const [imgMaior, setImgMaior] = useState<UploadFile[]>([]);

  const [previewOpenMenor, setPreviewOpenMenor] = useState(false);
  const [previewImgMenor, setPreviewImgMenor] = useState("");
  const [imgMenor, setImgMenor] = useState<UploadFile[]>([]);

  const inputInicial = useRef<any>(null);
  const inputNumero = useRef<any>(null);

  const editar = Object.values(empresaState.entity).length !== 0;

  //opções memorizadas dos selects
  const categoriaEmpresaOptions = useMemo(
    () => categoriaEmpresaState.list.map((a: any) => ({ value: a.id, label: a.denominacao })),
    [categoriaEmpresaState.list]
  );

  const grupoPlanoContaFinanceiroOptions = useMemo(
    () => grupoPlanoContaFinanceiroState.list.map((a: any) => ({ value: a.id, label: a.denominacao })),
    [grupoPlanoContaFinanceiroState.list]
  );

  const estadoOptions = useMemo(
    () => localizacaoState.listEstado.map((a: any) => ({ value: a.id, label: a.sigla })),
    [localizacaoState.listEstado]
  );

  const pessoaOptions = useMemo(
    () => pessoaState.list.map((a: PessoaModel) => ({ value: a.id, label: a.apelido, disabled: !a.ativo })),
    [pessoaState.list]
  );

  const empresaMarketingOptions = useMemo(
    () =>
      empresaState.list
        .filter((a: EmpresaModel) => a.idCategoriaEmpresa === 4)
        .map((a: EmpresaModel) => ({
          value: a.id,
          label: (
            <div className="optionSelectIcon">
              <Image width={25} height={25} src={a.urlLogotipoPequeno} preview={false} />
              {a.apelido}
            </div>
          ),
          textFilter: a.apelido,
          disabled: !a.ativo,
        })),
    [empresaState.list]
  );

  useEffect(() => {
    if (inputInicial.current && !editar) {
      inputInicial.current.focus();
    }
    dispatch(buscarPessoa({ ...pessoaState.busca, idCategoriaPessoa: "1", exibeContaPagar: true, exibeContaReceber: null }));
    if (editar) {
      dispatch(setTitle("EDITAR EMPRESA"));
      setTipo(empresaState.entity?.cpfcnpj?.length > 11);
      form.setFieldsValue({ ...empresaState.entity });
      form.setFieldValue("uf", empresaState.entity.idEstado);
      empresaState.entity.urlLogotipoGrande
        ? setImgMaior([
            {
              uid: "-1",
              name: "",
              status: "done",
              url: empresaState.entity.urlLogotipoGrande,
            },
          ])
        : setImgMaior([]);
      empresaState.entity.urlLogotipoPequeno
        ? setImgMenor([
            {
              uid: "-1",
              name: "",
              status: "done",
              url: empresaState.entity.urlLogotipoPequeno,
            },
          ])
        : setImgMenor([]);
      setIdCategoriaEmpresaSelecionado(empresaState.entity.idCategoriaEmpresa);
    } else {
      dispatch(setTitle("NOVA EMPRESA"));
    }
  }, []);

  const submit = async (value: any) => {
    let imgMaiorS3 = "";
    let imgMenorS3 = "";

    try {
      if (imgMaior.length !== 0) {
        imgMaiorS3 = await sendImageToAmazonS3(imgMaior[0]);
      }
      if (imgMenor.length !== 0) {
        imgMenorS3 = await sendImageToAmazonS3(imgMenor[0]);
      }
    } catch (e) {
      toastError("Ocorreu um erro ao fazer o upload do arquivo");
      return;
    }

    if (editar) {
      const editarEmpresa: EmpresaModel = { ...value };
      editarEmpresa.ativo = empresaState.entity.ativo;
      editarEmpresa.urlLogotipoGrande = imgMaiorS3;
      editarEmpresa.urlLogotipoPequeno = imgMenorS3;
      editarEmpresa.id = empresaState.entity.id;
      editarEmpresa.tipoPessoa = value.tipoPessoa;
      editarEmpresa.cep = value.cep?.replace(/[^0-9]/g, "");
      editarEmpresa.cpfcnpj = value.cpfcnpj?.replace(/[^0-9]/g, "");
      editarEmpresa.telefone = value.telefone?.replace(/[^0-9]/g, "");
      editarEmpresa.telefoneContato = value.telefoneContato?.replace(/[^0-9]/g, "");
      editarEmpresa.idOrigem = empresaState.entity.idOrigem;
      dispatch(atualizarEmpresa({ value: editarEmpresa, busca: empresaState.busca }));
    } else {
      const cadastroEmpresa: EmpresaDTOModel = { ...value };
      cadastroEmpresa.urlLogotipoGrande = imgMaiorS3;
      cadastroEmpresa.urlLogotipoPequeno = imgMenorS3;
      cadastroEmpresa.cep = value.cep?.replace(/[^0-9]/g, "");
      cadastroEmpresa.cpfcnpj = value.cpfcnpj?.replace(/[^0-9]/g, "");
      cadastroEmpresa.telefone = value.telefone?.replace(/[^0-9]/g, "");
      cadastroEmpresa.telefoneContato = value.telefoneContato?.replace(/[^0-9]/g, "");
      dispatch(cadastrarEmpresa({ value: cadastroEmpresa, busca: empresaState.busca }));
    }
  };

  const uploadButtonMaior = (
    <div className="uploadButtonMaior">
      <Icon.PlusOutlined style={{ display: "block" }} />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const uploadButtonMenor = (
    <div className="uploadButtonMenor">
      <Icon.PlusOutlined style={{ display: "block" }} />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const handleCancelMaior = () => setPreviewOpenMaior(false);

  const handleCancelMenor = () => setPreviewOpenMenor(false);

  const handlePreviewMaior = async (file: UploadFile) => {
    setPreviewImgMaior(file.url || (file.preview as string));
    setPreviewOpenMaior(true);
  };

  const handlePreviewMenor = async (file: UploadFile) => {
    setPreviewImgMenor(file.url || (file.preview as string));
    setPreviewOpenMenor(true);
  };

  const handleChangeMaior: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    setImgMaior(newFileList);
  };

  const handleChangeMenor: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    setImgMenor(newFileList);
  };

  const onBuscarCep = async () => {
    const retorno = await dispatch(buscarCep(inputCep?.replace(/[^0-9]/g, "")));

    if (!retorno.payload?.uf) {
      return;
    }

    const estado = localizacaoState.listEstado.find((a: EstadoModel) => a.sigla === retorno.payload.uf);

    form.setFieldsValue({
      endereco: retorno.payload.logradouro,
      bairro: retorno.payload.bairro,
      cidade: retorno.payload.localidade,
      idEstado: estado?.id,
    });
    inputNumero.current?.focus();
  };

  const handleTipochange = (value: any) => {
    value === "PJ" ? setTipo(true) : setTipo(false);
    form.setFieldValue("cpfcnpj", "");
  };

  const onChangeCategoriaEmpresa = (value: number) => {
    setIdCategoriaEmpresaSelecionado(value);
  };

  const voltar = () => {
    dispatch(setPageEmpresa("geral"));
  };

  return (
    <Flex gap={12} vertical>
      <Flex onClick={voltar} className="voltar">
        {"<"} VOLTAR
      </Flex>
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={12}>
          <Col flex="auto" span={8}>
            <Form.Item
              label="Razão Social"
              name="razaoSocial"
              rules={[
                {
                  required: true,
                  message: "Digite a razão social",
                  validateTrigger: "onSubmit",
                },
              ]}
            >
              <Input type="text" ref={inputInicial} />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item
              label="Nome Fantasia"
              name="apelido"
              rules={[
                {
                  required: true,
                  message: "Digito o nome fantasia da empresa",
                  validateTrigger: "onSubmit",
                },
              ]}
            >
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item
              label="Categoria da empresa"
              name="idCategoriaEmpresa"
              rules={[
                {
                  required: true,
                  message: "Selecione uma categoria",
                },
              ]}
            >
              <Select
                autoFocus
                showSearch
                optionFilterProp="value"
                onChange={onChangeCategoriaEmpresa}
                options={categoriaEmpresaOptions}
              />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item
              label="Grupo de plano de conta financeiro"
              name="idGrupoPlanoContaFinanceiro"
              rules={[
                {
                  required: true,
                  message: "Selecione um grupo",
                },
              ]}
            >
              <Select
                autoFocus
                showSearch
                optionFilterProp="label"
                options={grupoPlanoContaFinanceiroOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col flex="auto" span={3}>
            <Form.Item
              label="Tipo Pessoa"
              name="tipoPessoa"
              rules={[
                {
                  required: true,
                  message: "Selecione o tipo pessoa",
                  validateTrigger: "onSubmit",
                },
              ]}
            >
              <Select
                autoFocus
                optionFilterProp="label"
                onChange={handleTipochange}
                options={[
                  {
                    value: "PF",
                    label: "PF",
                  },
                  {
                    value: "PJ",
                    label: "PJ",
                  },
                ]}
              />
            </Form.Item>
          </Col>
          <Col flex="auto" span={5}>
            <Form.Item
              label="CPF/CNPJ"
              name="cpfcnpj"
              rules={[
                {
                  required: true,
                  message: "Digite o CPF ou o CNPJ",
                },
              ]}
            >
              {tipo ? <MaskedInput mask="00.000.000/0000-00"></MaskedInput> : <MaskedInput mask="000.000.000-00"></MaskedInput>}
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item label="Inscrição Estadual" name="inscricaoEstadual">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item label="Inscrição Municipal" name="inscricaoMunicipal">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col flex="auto" span={6}>
            <Form.Item label="CEP" name="cep">
              <MaskedInput mask={mascaraCep()} onChange={(e) => setInputCep(e.target.value)} />
            </Form.Item>
          </Col>
          <Col className="colCepButton">
            <Form.Item label=" ">
              <Button className="cepButton" onClick={onBuscarCep}>
                Buscar
              </Button>
            </Form.Item>
          </Col>
          <Col flex="auto" span={14}>
            <Form.Item label="Endereço" name="endereco">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col flex="auto" span={4}>
            <Form.Item label="Número" name="numero">
              <Input type="text" ref={inputNumero} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col flex="auto" span={6}>
            <Form.Item label="Complemento" name="complemento">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item label="Bairro" name="bairro">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item label="Cidade" name="cidade">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col flex="auto" span={2}>
            <Form.Item
              label="UF"
              name="idEstado"
              rules={[
                {
                  required: true,
                  message: "Selecione o estado",
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={localizacaoState.loadingEstado}
                options={estadoOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col flex="auto" span={4}>
            <Form.Item label="Telefone" name="telefone">
              <MaskedInput mask={mascaraCelular()} />
            </Form.Item>
          </Col>
          <Col flex="auto" span={10}>
            <Form.Item label="Site" name="homepage">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col flex="auto" span={10}>
            <Form.Item label="E-mail Institucional" name="email">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col flex="auto" span={24}>
            <Form.Item label="Observação" name="observacao">
              <TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" orientationMargin="0">
          Associação de fornecedores
        </Divider>
        <Row gutter={12}>
          <Col flex="auto" span={8}>
            <Form.Item label="Capitalização" name="idPessoaCapitalizacao">
              <Select
                showSearch
                optionFilterProp="label"
                options={pessoaOptions}
              />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item label="Entidade" name="idPessoaEntidade">
              <Select
                showSearch
                optionFilterProp="label"
                options={pessoaOptions}
              />
            </Form.Item>
          </Col>
          {idCategoriaEmpresaSelecionado === 1 && (
            <Col flex="auto" span={8}>
              <Form.Item label="Empresa de Marketing" name="idEmpresaMarketing">
                <Select
                  showSearch
                  optionFilterProp="textFilter"
                  options={empresaMarketingOptions}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Divider orientation="left" orientationMargin="0">
          Informações de contato
        </Divider>
        <Row gutter={12}>
          <Col flex="auto" span={8}>
            <Form.Item label="Contato" name="nomeContato">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item label="Telefone" name="telefoneContato">
              <MaskedInput mask={mascaraCelular()} />
            </Form.Item>
          </Col>
          <Col flex="auto" span={8}>
            <Form.Item label="E-mail" name="emailContato">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" orientationMargin="0">
          Logotipos
        </Divider>
        <Row gutter={12}>
          <Col flex="auto" span={6}>
            <Upload
              onChange={handleChangeMaior}
              beforeUpload={() => {
                return false;
              }}
              listType="picture-card"
              fileList={imgMaior}
              onPreview={handlePreviewMaior}
            >
              {imgMaior.length >= 1 ? null : uploadButtonMaior}
            </Upload>
            <Modal open={previewOpenMaior} title={"Logotipo maior"} footer={null} onCancel={handleCancelMaior} destroyOnHidden>
              <img alt="" style={{ width: "100%" }} src={previewImgMaior} />
            </Modal>
          </Col>
          <Col flex="auto" span={6} className="uploadTextMaior">
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px" }}>
              <p className="labelLogotipo">Logotipo maior</p>
              <Tooltip title="Alterações feitas no logotipo maior só serão efetivamente aplicadas à empresa após clicar em 'Salvar'">
                <Icon.InfoCircleOutlined />
              </Tooltip>
            </div>
            <p className="textLogotipo">Faça o upload do logotipo que aparecerá no menu lateral dos usuários daquela empresa. Tamanho recomendado 180px x 60px</p>
          </Col>
          <Col flex="auto" span={6} className="imagemMenor">
            <Upload
              onChange={handleChangeMenor}
              beforeUpload={() => {
                return false;
              }}
              listType="picture-card"
              fileList={imgMenor}
              onPreview={handlePreviewMenor}
            >
              {imgMenor.length >= 1 ? null : uploadButtonMenor}
            </Upload>
            <Modal open={previewOpenMenor} title={"Logotipo menor"} footer={null} onCancel={handleCancelMenor} destroyOnHidden>
              <img alt="" style={{ width: "100%" }} src={previewImgMenor} />
            </Modal>
          </Col>
          <Col flex="auto" span={6} className="uploadTextMenor">
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px" }}>
              <p className="labelLogotipo">Logotipo menor</p>
              <Tooltip title="Alterações feitas no logotipo menor só serão efetivamente aplicadas à empresa após clicar em 'Salvar'">
                <Icon.InfoCircleOutlined />
              </Tooltip>
            </div>
            <p className="textLogotipo">Faça o upload do logotipo que aparecerá em alguns filros utilizados nas telas para selecionar a empresa. Tamanho recomendado 36px x 36px</p>
          </Col>
        </Row>
        <Divider />
        <div className="modalFooter">
          <Space size="middle">
            <Button className="cancelButton" onClick={voltar} icon={<Icon.LeftOutlined />}>
              VOLTAR
            </Button>
            <Button className="buttonPrimary" type="primary" htmlType="submit" icon={<Icon.CheckOutlined />}>
              SALVAR
            </Button>
          </Space>
        </div>
      </Form>
    </Flex>
  );
};

export default CadastrarPage;
